// packages/web/src/components/Slots/PlayerSlotsList.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
  Chip,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  CircularProgress,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import TuneIcon from '@mui/icons-material/Tune';
import { PlayerSlot, TimeRange, formatPlayerRatings, Challenge, ChallengeService, PlayerSlotService, Filters } from '@matchpoint/shared';
import { formatTimeRange, formatDate, calculateEndTime } from '@matchpoint/shared';
import { useAuth } from '../../hooks/useAuth';
import { LoginDialog } from '../Auth/LoginDialog';

const SlotListContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  width: '100%',
}));

const SlotItem = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(0.75, 0),
  padding: theme.spacing(1.5),
  border: '1px solid #e0e0e0',
  borderRadius: '6px',
  transition: 'all 0.2s ease-in-out',
  backgroundColor: '#ffffff',
  '&:hover': {
    borderColor: theme.palette.primary.light,
    boxShadow: '0 2px 8px rgba(76, 175, 80, 0.1)',
    transform: 'translateY(-1px)',
  }
}));

const SlotHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1),
  gap: theme.spacing(1.5),
}));

const PlayerInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flex: 1,
}));

const CompactAvatar = styled(Avatar)(({ theme }) => ({
  width: 40,
  height: 40,
  backgroundColor: '#84b817',
  fontSize: '1rem',
  fontWeight: 600,
}));

const SlotDetails = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  marginLeft: theme.spacing(5),
}));

const DetailItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
}));

const DetailIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  }
}));

const DetailText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: '#666',
  lineHeight: 1.6,
  fontWeight: 400,
}));

const CompactChip = styled(Chip)(({ theme }) => ({
  height: 20,
  fontSize: '0.7rem',
  '& .MuiChip-label': {
    padding: '0 6px',
  },
  '& .MuiChip-icon': {
    fontSize: '0.75rem',
    marginLeft: '4px',
  },
}));

const RatingChip = styled(CompactChip)<{ ratings?: any }>(({ theme, ratings }) => {
  const hasUTR = ratings?.utr && ratings.utr > 0;
  const hasNTRP = ratings?.ntrp && ratings.ntrp > 0;
  
  if (hasUTR && hasNTRP) {
    return {
      backgroundColor: '#4caf50',
      color: 'white',
      fontWeight: 600,
    };
  } else if (hasUTR || hasNTRP) {
    return {
      backgroundColor: '#8bc34a',
      color: 'white',
      fontWeight: 500,
    };
  } else {
    return {
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.getContrastText(theme.palette.grey[300]),
    };
  }
});

const ChallengeButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#c4d600',
  color: '#1a1a1a',
  padding: '6px 16px',
  fontSize: '0.875rem',
  minWidth: 90,
  height: 32,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(196, 214, 0, 0.2)',
  '&:hover': {
    backgroundColor: '#b3c200',
    boxShadow: '0 4px 12px rgba(196, 214, 0, 0.3)',
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.2s ease',
  '&:disabled': {
    backgroundColor: '#e0e0e0',
    color: '#999',
  },
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1.5, 0),
  borderTop: `1px solid ${theme.palette.divider}`,
  marginTop: theme.spacing(1.5),
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

const PaginationControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

const CompactFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    fontSize: '0.75rem',
  },
  '& .MuiInputBase-root': {
    fontSize: '0.75rem',
  },
  '& .MuiOutlinedInput-input': {
    padding: '6px 10px',
  },
  '& .MuiInputLabel-outlined': {
    transform: 'translate(10px, 8px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
  },
}));

const CompactAlert = styled(Alert)(({ theme }) => ({
  padding: '6px 12px',
  '& .MuiAlert-icon': {
    fontSize: '1.25rem',
    marginRight: '8px',
  },
  '& .MuiAlert-message': {
    fontSize: '0.75rem',
  },
}));

const SlotsHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
  padding: theme.spacing(0, 1),
}));

const SlotsTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 700,
  color: '#1a1a1a',
  letterSpacing: '-0.01em',
}));

const SlotsCount = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light + '20',
  color: theme.palette.primary.main,
  height: 22,
  fontSize: '0.7rem',
  fontWeight: 500,
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

const FilterButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  padding: '4px 12px',
  fontSize: '0.75rem',
  minHeight: 28,
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: '6px',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  '& .MuiButton-startIcon': {
    marginRight: '4px',
    '& > svg': {
      fontSize: '1rem',
    },
  },
}));

const CompactDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '12px',
    border: '1px solid #e5e5e5',
  },
  '& .MuiDialogTitle-root': {
    fontSize: '1.125rem',
    fontWeight: 700,
    padding: theme.spacing(2, 2.5),
    paddingBottom: theme.spacing(1.5),
    color: '#1a1a1a',
    letterSpacing: '-0.01em',
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(0, 2.5, 2.5),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1.5, 2.5),
  },
}));

interface PlayerSlotsListProps {
  slots: PlayerSlot[];
  loading: boolean;
  timeRange: TimeRange | null;
  onChallenge: (slot: PlayerSlot, message: string) => Promise<void>;
  onSlotsUpdate?: () => void;
  displayTimezone?: string;
  onLoginClick?: () => void;
  filters?: Filters;
  userLocation?: { latitude: number; longitude: number } | null;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  onFilterClick?: () => void;
}

// Timezone conversion helper function
const convertSlotToDisplayTimezone = (slotTime: Date, sourceTimezone: string, displayTimezone: string): Date => {
  try {
    if (sourceTimezone === displayTimezone) {
      return new Date(slotTime);
    }
    
    const sourceFormatter = new Intl.DateTimeFormat('en', {
      timeZone: sourceTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    
    const year = slotTime.getFullYear();
    const month = slotTime.getMonth();
    const date = slotTime.getDate();
    const hours = slotTime.getHours();
    const minutes = slotTime.getMinutes();
    const seconds = slotTime.getSeconds();
    
    const tempUtc = new Date(Date.UTC(year, month, date, hours, minutes, seconds));
    
    const sourceParts = sourceFormatter.formatToParts(tempUtc);
    const sourceObj = sourceParts.reduce((acc, part) => {
      if (part.type !== 'literal') acc[part.type] = part.value;
      return acc;
    }, {} as any);
    
    const sourceTime = new Date(
      parseInt(sourceObj.year),
      parseInt(sourceObj.month) - 1,
      parseInt(sourceObj.day),
      parseInt(sourceObj.hour),
      parseInt(sourceObj.minute),
      parseInt(sourceObj.second)
    );
    
    const wantedTime = new Date(year, month, date, hours, minutes, seconds);
    const adjustment = wantedTime.getTime() - sourceTime.getTime();
    const correctedUtc = new Date(tempUtc.getTime() + adjustment);
    
    const targetFormatter = new Intl.DateTimeFormat('en', {
      timeZone: displayTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    
    const targetParts = targetFormatter.formatToParts(correctedUtc);
    const targetObj = targetParts.reduce((acc, part) => {
      if (part.type !== 'literal') acc[part.type] = part.value;
      return acc;
    }, {} as any);
    
    return new Date(
      parseInt(targetObj.year),
      parseInt(targetObj.month) - 1,
      parseInt(targetObj.day),
      parseInt(targetObj.hour),
      parseInt(targetObj.minute),
      parseInt(targetObj.second)
    );
    
  } catch (error) {
    console.warn('Timezone conversion failed, using original time:', error);
    return new Date(slotTime);
  }
};

export const PlayerSlotsList: React.FC<PlayerSlotsListProps> = ({
  slots,
  loading,
  timeRange,
  onChallenge,
  onSlotsUpdate,
  displayTimezone: propDisplayTimezone,
  onLoginClick,
  filters,
  userLocation,
  onFilterClick,
}) => {
  const { user, signIn, signUp } = useAuth();
  const [selectedSlot, setSelectedSlot] = useState<PlayerSlot | null>(null);
  const [challengeMessage, setChallengeMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [challengeDialogError, setChallengeDialogError] = useState<string | null>(null);
  const [filteredSlots, setFilteredSlots] = useState<PlayerSlot[]>([]);
  const [challengingSlots, setChallenginSlots] = useState<Set<string>>(new Set());
  const [pendingChallenges, setPendingChallenges] = useState<Challenge[]>([]);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  
  // NEW: Progressive loading state
  const [loadingCourts, setLoadingCourts] = useState(false);
  const [enrichedSlotIds, setEnrichedSlotIds] = useState<Set<string>>(new Set());
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const displayTimezone = propDisplayTimezone || timeRange?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Load user's pending challenges
  useEffect(() => {
    const loadPendingChallenges = async () => {
      if (!user) {
        setPendingChallenges([]);
        return;
      }
  
      try {
        const challengeService = ChallengeService.getInstance();
        await challengeService['firebaseService'].initializeApp();
        
        const sentChallenges = await challengeService.getSentChallenges(user.uid);
        const activeOrPending = sentChallenges.filter(challenge => 
          challenge.status === 'pending' || challenge.status === 'accepted'
        );
        setPendingChallenges(activeOrPending);
      } catch (error) {
        console.error('Error loading pending challenges:', error);
        setPendingChallenges([]);
      }
    };
  
    loadPendingChallenges();
  }, [user]);

  // NEW: Progressive enrichment function (no filtering, just enrichment)
  const enrichSlotsProgressively = async (slotsToEnrich: PlayerSlot[]) => {
    // Only enrich slots that haven't been enriched yet and don't have coordinates
    const unenrichedSlots = slotsToEnrich.filter(
      slot => !enrichedSlotIds.has(slot.id) && !slot.location?.coordinates
    );
    
    if (unenrichedSlots.length === 0) return;
    
    setLoadingCourts(true);
    
    try {
      const playerSlotService = PlayerSlotService.getInstance();
      
      // Enrich in batches of 20 for progressive updates
      const batchSize = 20;
      for (let i = 0; i < unenrichedSlots.length; i += batchSize) {
        const batch = unenrichedSlots.slice(i, i + batchSize);
        const enriched = await (playerSlotService as any).enrichSlotsWithCoordinatesBatch(batch, batchSize);
        
        // Update slots with enriched data (no filtering)
        setFilteredSlots(prevSlots => {
          const updatedSlots = prevSlots.map(slot => {
            const enrichedSlot = enriched.find((e: PlayerSlot) => e.id === slot.id);
            return enrichedSlot || slot;
          });
          return updatedSlots;
        });
        
        // Track enriched slot IDs
        setEnrichedSlotIds(prev => {
          const newSet = new Set(prev);
          batch.forEach(slot => newSet.add(slot.id));
          return newSet;
        });
      }
    } catch (error) {
      console.error('Error enriching slots:', error);
    } finally {
      setLoadingCourts(false);
    }
  };

  // TWO-DIMENSIONAL FILTERING with progressive enrichment
  useEffect(() => {
    if (!timeRange) {
      setFilteredSlots(slots);
      
      // Note: No need to enrich for radius filtering anymore
      // Radius filtering is now handled at the database level in the service
      
      return;
    }
    
    // Extract the two dimensions from the selection
    const startDate = new Date(timeRange.startDate);
    const endDate = new Date(timeRange.endDate);
    
    // Dimension 1: Date range (which days are included)
    const selectionStartDay = new Date(startDate);
    selectionStartDay.setHours(0, 0, 0, 0);
    
    const selectionEndDay = new Date(endDate);
    selectionEndDay.setHours(23, 59, 59, 999);
    
    // Dimension 2: Time window (what time of day)
    const startTimeWindow = {
      hours: startDate.getHours(),
      minutes: startDate.getMinutes()
    };
    
    const endTimeWindow = {
      hours: endDate.getHours(),
      minutes: endDate.getMinutes()
    };
    
    console.log('Two-dimensional filtering:', {
      dateRange: `${selectionStartDay.toDateString()} to ${selectionEndDay.toDateString()}`,
      timeWindow: `${startTimeWindow.hours}:${String(startTimeWindow.minutes).padStart(2, '0')} to ${endTimeWindow.hours}:${String(endTimeWindow.minutes).padStart(2, '0')}`,
      timezone: timeRange.timezone || displayTimezone
    });
    
    const filtered = slots.filter(slot => {
      // Get slot time in display timezone if needed
      let slotDisplayTime: Date;
      if (slot.timezone && slot.timezone !== displayTimezone) {
        slotDisplayTime = convertSlotToDisplayTimezone(slot.startTime, slot.timezone, displayTimezone);
      } else {
        slotDisplayTime = new Date(slot.startTime);
      }
      
      // Check Dimension 1: Is the slot date within the selected date range?
      const slotDateOnly = new Date(slotDisplayTime);
      slotDateOnly.setHours(0, 0, 0, 0);
      
      if (slotDateOnly < selectionStartDay || slotDateOnly > selectionEndDay) {
        return false; // Outside date range
      }
      
      // Check Dimension 2: Is the slot time within the selected time window?
      const slotStartHour = slotDisplayTime.getHours();
      const slotStartMinute = slotDisplayTime.getMinutes();
      const slotEndTime = new Date(slotDisplayTime.getTime() + slot.duration * 60000);
      const slotEndHour = slotEndTime.getHours();
      const slotEndMinute = slotEndTime.getMinutes();
      
      // Convert to minutes since midnight for easier comparison
      const slotStartMinutes = slotStartHour * 60 + slotStartMinute;
      const slotEndMinutes = slotEndHour * 60 + slotEndMinute;
      const windowStartMinutes = startTimeWindow.hours * 60 + startTimeWindow.minutes;
      const windowEndMinutes = endTimeWindow.hours * 60 + endTimeWindow.minutes;
      
      // Check if the slot time overlaps with the time window
      let timeMatches = false;
      
      if (windowStartMinutes <= windowEndMinutes) {
        // Normal case: time window doesn't cross midnight
        timeMatches = slotStartMinutes < windowEndMinutes && slotEndMinutes > windowStartMinutes;
      } else {
        // Time window crosses midnight
        timeMatches = (slotStartMinutes >= windowStartMinutes || slotEndMinutes >= windowStartMinutes) ||
                      (slotStartMinutes <= windowEndMinutes || slotEndMinutes <= windowEndMinutes);
      }
      
      return timeMatches;
    });
    
    console.log(`Filtered to ${filtered.length} slots from ${slots.length} total`);
    setFilteredSlots(filtered);
    
    // Note: No need to enrich for radius filtering anymore
    // Radius filtering is now handled at the database level in the service
  }, [slots, timeRange, displayTimezone]);

  // Reset to first page when slots change
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredSlots, slots]);

  const isSlotAlreadyChallenged = (slotId: string): { challenged: boolean; status?: string } => {
    const challenge = pendingChallenges.find(challenge => challenge.slotId === slotId);
    return {
      challenged: !!challenge,
      status: challenge?.status
    };
  };
  
  const handleChallengeClick = (slot: PlayerSlot) => {
    if (!user) {
      setLoginDialogOpen(true);
      return;
    }
    
    if (user.uid === slot.playerId) {
      alert("You cannot challenge yourself");
      return;
    }
    
    const challengeCheck = isSlotAlreadyChallenged(slot.id);

    if (challengeCheck.challenged) {
      alert("You already have a pending challenge for this time slot");
      return;
    }
    
    if (challengingSlots.has(slot.id)) {
      return;
    }
    
    setSelectedSlot(slot);
    setChallengeMessage(`Hi ${slot.playerName}! I'd like to play tennis with you.`);
    setError(null);
    setChallengeDialogError(null);
  };
  
  const handleClose = () => {
    setSelectedSlot(null);
    setChallengeMessage('');
    setError(null);
    setChallengeDialogError(null);
  };
  
  const handleSubmit = async () => {
    if (!selectedSlot) return;
    
    setIsSubmitting(true);
    setError(null);
    setChallengeDialogError(null);
    
    setChallenginSlots(prev => new Set(prev).add(selectedSlot.id));
    
    try {
      await onChallenge(selectedSlot, challengeMessage);
      
      if (user) {
        const challengeService = ChallengeService.getInstance();
        const sentChallenges = await challengeService.getSentChallenges(user.uid);
        const activeOrPending = sentChallenges.filter(challenge => 
          challenge.status === 'pending' || challenge.status === 'accepted'
        );
        setPendingChallenges(activeOrPending);
      }
      
      if (onSlotsUpdate) {
        onSlotsUpdate();
      }
      
      handleClose();
    } catch (err) {
      console.error('Challenge error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to send challenge. Please try again.';
      setChallengeDialogError(errorMessage);
    } finally {
      setIsSubmitting(false);
      setChallenginSlots(prev => {
        const newSet = new Set(prev);
        newSet.delete(selectedSlot.id);
        return newSet;
      });
    }
  };

  const handleSignIn = async (email: string, password: string) => {
    try {
      await signIn(email, password);
      setLoginDialogOpen(false);
    } catch (error) {
      console.error('Sign in error:', error);
    }
  };

  const handleSignUp = async (email: string, password: string, name: string) => {
    try {
      await signUp(email, password, name);
      setLoginDialogOpen(false);
    } catch (error) {
      console.error('Sign up error:', error);
    }
  };

  // IMPROVED TIME RANGE DISPLAY
  const renderTimeRangeInfo = () => {
    if (!timeRange) return null;
    
    const startDate = new Date(timeRange.startDate);
    const endDate = new Date(timeRange.endDate);
    
    const dateFormatter = new Intl.DateTimeFormat('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
    
    const formatTime = (date: Date) => {
      const hours = date.getHours();
      const minutes = date.getMinutes();
      return `${hours}:${String(minutes).padStart(2, '0')}`;
    };
    
    const startDateStr = dateFormatter.format(startDate);
    const endDateStr = dateFormatter.format(endDate);
    const startTimeStr = formatTime(startDate);
    const endTimeStr = formatTime(endDate);
    
    const sameDay = startDate.toDateString() === endDate.toDateString();
    
    return (
      <CompactAlert severity="info" sx={{ mb: 1.5 }}>
        <Typography variant="body2" component="div" sx={{ fontSize: '0.75rem' }}>
          <strong>Showing slots:</strong> {
            sameDay 
              ? `${startDateStr} • ${startTimeStr} - ${endTimeStr}`
              : <>
                  {startDateStr} to {endDateStr}<br/>
                  <strong>Daily time window:</strong> {startTimeStr} - {endTimeStr}
                </>
          } ({timeRange.timezone || displayTimezone})
        </Typography>
      </CompactAlert>
    );
  };
  
  // Use filteredSlots when there's a timeRange, otherwise use all slots
  const displaySlots = timeRange ? filteredSlots : filteredSlots;
  
  // Pagination - only show current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSlots = displaySlots.slice(startIndex, endIndex);
  const totalPages = Math.ceil(displaySlots.length / itemsPerPage);
  
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };
  
  const handleItemsPerPageChange = (event: any) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1);
  };
  
  if (loading) {
    return (
      <SlotListContainer sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress size={30} />
      </SlotListContainer>
    );
  }
  
  if (displaySlots.length === 0) {
    return (
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column' }}>
        {renderTimeRangeInfo()}
        
        <SlotsHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SlotsTitle>
              Available Players (0)
            </SlotsTitle>
            
            {onFilterClick && (
              <IconButton 
                onClick={onFilterClick}
                size="small"
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '6px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <TuneIcon sx={{ fontSize: '1.1rem' }} />
              </IconButton>
            )}
          </Box>
        </SlotsHeader>
        
        <Paper sx={{ p: 2.5, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            No player slots available for the selected filters
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
            Try adjusting your filters or selecting a different time range
          </Typography>
        </Paper>
      </Box>
    );
  }
  
  return (
    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column' }}>
      {renderTimeRangeInfo()}
      
      {/* NEW: Loading indicator for court enrichment */}
      {loadingCourts && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Loading court details...
          </Typography>
        </Box>
      )}
      
      <SlotsHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SlotsTitle>
            Available Players ({displaySlots.length})
          </SlotsTitle>
          
          {onFilterClick && (
            <IconButton 
              onClick={onFilterClick}
              size="small"
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                padding: '6px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <TuneIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          )}
        </Box>
        
        {displaySlots.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {startIndex + 1}-{Math.min(endIndex, displaySlots.length)} of {displaySlots.length}
          </Typography>
        )}
      </SlotsHeader>
      
      <SlotListContainer>
        {paginatedSlots.map((slot) => {
          let displayTime: Date;
          let displayEndTime: Date;
          
          if (slot.timezone && slot.timezone !== displayTimezone) {
            displayTime = convertSlotToDisplayTimezone(slot.startTime, slot.timezone, displayTimezone);
            displayEndTime = new Date(displayTime.getTime() + slot.duration * 60000);
          } else {
            displayTime = slot.startTime;
            displayEndTime = new Date(displayTime.getTime() + slot.duration * 60000);
          }
          
          const isChallengingThisSlot = challengingSlots.has(slot.id);
          const isAlreadyChallenged = isSlotAlreadyChallenged(slot.id);
          const isOwnSlot = user?.uid === slot.playerId;
          
          return (
            <SlotItem key={slot.id} elevation={0}>
              <SlotHeader>
                <PlayerInfo>
                  <CompactAvatar>
                    {slot.playerName.charAt(0).toUpperCase()}
                  </CompactAvatar>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                      {slot.playerName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <RatingChip 
                        label={formatPlayerRatings(slot.playerRatings || { utr: null, ntrp: null })} 
                        size="small" 
                        ratings={slot.playerRatings}
                      />
                      {isAlreadyChallenged.challenged && (
                        <CompactChip 
                          icon={isAlreadyChallenged.status === 'accepted' ? <CheckCircleIcon /> : <HourglassEmptyIcon />}
                          label={isAlreadyChallenged.status === 'accepted' ? 'Scheduled' : 'Pending'} 
                          size="small" 
                          color={isAlreadyChallenged.status === 'accepted' ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                </PlayerInfo>
                
                {user ? (
                  !isOwnSlot && !isAlreadyChallenged.challenged ? (
                    <ChallengeButton 
                      variant="contained" 
                      color="primary" 
                      onClick={() => handleChallengeClick(slot)}
                      disabled={isChallengingThisSlot}
                      size="small"
                    >
                      {isChallengingThisSlot ? (
                        <>
                          <CircularProgress size={12} sx={{ mr: 0.5, color: 'white' }} />
                          Sending...
                        </>
                      ) : (
                        'Challenge'
                      )}
                    </ChallengeButton>
                  ) : isAlreadyChallenged.challenged && (
                    <ChallengeButton 
                      variant="outlined" 
                      disabled
                      size="small"
                      color={isAlreadyChallenged.status === 'accepted' ? 'success' : 'warning'}
                    >
                      {isAlreadyChallenged.status === 'accepted' ? 'Scheduled' : 'Pending'}
                    </ChallengeButton>
                  )
                ) : (
                  !isAlreadyChallenged.challenged && (
                    <ChallengeButton 
                      variant="contained" 
                      color="primary" 
                      onClick={() => handleChallengeClick(slot)}
                      size="small"
                    >
                      Challenge
                    </ChallengeButton>
                  )
                )}
              </SlotHeader>
              
              <SlotDetails>
                <DetailItem>
                  <DetailIcon><AccessTimeIcon /></DetailIcon>
                  <DetailText>
                    {formatDate(displayTime)} • {formatTimeRange(displayTime, displayEndTime)} ({slot.duration}m)
                    
                    {slot.timezone && slot.timezone !== displayTimezone && (
                      <Typography component="span" sx={{ fontSize: '0.7rem', color: 'text.disabled', ml: 0.5 }}>
                        (orig: {formatTimeRange(
                          slot.startTime, 
                          new Date(slot.startTime.getTime() + slot.duration * 60000)
                        )} {slot.timezone})
                      </Typography>
                    )}
                  </DetailText>
                </DetailItem>
                
                <DetailItem>
                  <DetailIcon><LocationOnIcon /></DetailIcon>
                  <DetailText>
                    {slot.court}{slot.city ? `, ${slot.city}` : ''}{slot.country ? `, ${slot.country}` : ''}
                  </DetailText>
                </DetailItem>
              </SlotDetails>
            </SlotItem>
          );
        })}
      </SlotListContainer>
      
      {/* Pagination Controls */}
      {displaySlots.length > 0 && totalPages > 1 && (
        <PaginationContainer>
          <PaginationControls>
            <CompactFormControl size="small" sx={{ minWidth: 80 }}>
              <InputLabel>Per page</InputLabel>
              <Select
                value={itemsPerPage}
                label="Per page"
                onChange={handleItemsPerPageChange}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </CompactFormControl>
            
            <Typography variant="caption" color="text.secondary">
              Page {currentPage} of {totalPages}
            </Typography>
          </PaginationControls>
          
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            size="small"
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                fontSize: '0.75rem',
                minWidth: 28,
                height: 28,
              },
            }}
          />
        </PaginationContainer>
      )}
      
      {/* Challenge Dialog */}
      <CompactDialog open={!!selectedSlot} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>
          Challenge {selectedSlot?.playerName}
        </DialogTitle>
        <DialogContent>
          {challengeDialogError && (
            <CompactAlert severity="error" sx={{ mb: 1.5 }}>
              {challengeDialogError}
            </CompactAlert>
          )}
          
          {selectedSlot && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {formatDate(selectedSlot.startTime)} at {formatTimeRange(
                  selectedSlot.startTime,
                  new Date(selectedSlot.startTime.getTime() + selectedSlot.duration * 60000)
                )} • {selectedSlot.court}
              </Typography>
            </Box>
          )}
          
          <TextField
            autoFocus
            margin="dense"
            label="Message"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={challengeMessage}
            onChange={(e) => setChallengeMessage(e.target.value)}
            placeholder="Write a message to the player..."
            sx={{
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} size="small">Cancel</Button>
          <Button 
            onClick={handleSubmit}
            disabled={isSubmitting || !challengeMessage.trim()}
            variant="contained"
            size="small"
          >
            {isSubmitting ? 'Sending...' : 'Send Challenge'}
          </Button>
        </DialogActions>
      </CompactDialog>
      
      {/* Login Dialog */}
      <LoginDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
      />
    </Box>
  );
};